// webinarPublisher: host-side "Go Live" — publishes the presenter's media to
// the server's webinar engine (WebRTC ingest). The server transcodes to HLS,
// so this connection is upstream-only and costs the presenter ~3 Mbps.

export interface WebinarPublishResult {
  webinarId: string;
}

function iceServersFrom(): RTCIceServer[] {
  // the mesh manager's ice servers are set from join data; reuse via the room's
  // stored manager when available
  const w = window as any;
  if (w.__verveWebRTC && w.__verveWebRTC['iceServers']) return w.__verveWebRTC['iceServers'];
  return [{ urls: ['stun:stun.l.google.com:19302', 'stun:stun.cloudflare.com:3478'] }];
}

function waitIceGathering(pc: RTCPeerConnection, maxMs = 2500): Promise<void> {
  if (pc.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => resolve();
    pc.addEventListener('icegatheringstatechange', () => {
      if (pc.iceGatheringState === 'complete') done();
    });
    setTimeout(done, maxMs);
  });
}

// Publish the given stream to a (new or existing) webinar for this room.
export async function publishWebinar(
  roomId: string,
  hostToken: string,
  stream: MediaStream,
  existingWebinarId?: string,
): Promise<WebinarPublishResult> {
  let webinarId = existingWebinarId || '';

  if (!webinarId) {
    const res = await fetch('/api/v1/webinars', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ room_id: roomId, host_token: hostToken }),
    });
    if (!res.ok) throw new Error(`Failed to create webinar (${res.status})`);
    const data = await res.json();
    webinarId = data.webinar_id;
  }

  const pc = new RTCPeerConnection({ iceServers: iceServersFrom() });
  for (const track of stream.getTracks()) {
    pc.addTrack(track, stream);
  }
  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);
  await waitIceGathering(pc);

  const res = await fetch(`/api/v1/webinars/${webinarId}/publish`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sdp: pc.localDescription!.sdp, type: 'offer', host_token: hostToken }),
  });
  if (!res.ok) {
    const txt = await res.text();
    pc.close();
    throw new Error(`Publish failed: ${txt.slice(0, 120)}`);
  }
  const answer = await res.json();
  await pc.setRemoteDescription({ type: 'answer', sdp: answer.sdp });

  return { webinarId };
}

export async function stopWebinar(webinarId: string, hostToken: string): Promise<void> {
  await fetch(`/api/v1/webinars/${webinarId}/stop`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ host_token: hostToken }),
  }).catch(() => {});
}
